/* tslint:disable */
/* eslint-disable */
import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { AccountBalance, CreditCard, Payment } from '@material-ui/icons';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCommonStyles } from './AdminConsoleBilling';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';

type Props = {
  paymentMethod: any | null;
  shouldAlert?: boolean;
};

const humaniseType = (type?: string) =>
  (type ?? 'Payment method').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Stripe exposes a different sub-object per payment method type, so each needs its own label.
const describePaymentMethod = (paymentMethod: any): { title: string; subtitle?: string } => {
  const type = paymentMethod?.type;

  if (type === 'card' && paymentMethod.card) {
    const { last4, brand, exp_month: expMonth, exp_year: expYear } = paymentMethod.card;
    return {
      title: `•••• ${last4}`,
      subtitle: [brand, expMonth && expYear ? `• Expires ${expMonth}/${expYear}` : undefined].filter(Boolean).join(' '),
    };
  }

  if (type === 'link') {
    const email = paymentMethod.link?.email;
    return email ? { title: email, subtitle: 'Link' } : { title: 'Link' };
  }

  if (type === 'au_becs_debit' && paymentMethod.au_becs_debit) {
    return { title: `•••• ${paymentMethod.au_becs_debit.last4}`, subtitle: 'BECS Direct Debit' };
  }

  if (type === 'us_bank_account' && paymentMethod.us_bank_account) {
    const bank = paymentMethod.us_bank_account;
    return { title: `•••• ${bank.last4}`, subtitle: [bank.bank_name, 'Bank account'].filter(Boolean).join(' • ') };
  }

  if (type === 'sepa_debit' && paymentMethod.sepa_debit) {
    return { title: `•••• ${paymentMethod.sepa_debit.last4}`, subtitle: 'SEPA Direct Debit' };
  }

  return { title: humaniseType(type) };
};

export const BillingMethod = (props: Props) => {
  const classes = useCommonStyles();
  const { theme } = useCampfireTheme();
  const { paymentMethod, shouldAlert = false } = props;
  const [isLoading, setIsLoading] = useState(false);
  const apiUrl = useApiUrl();
  const customerPortalFetch = useCampfireFetch<{ url: string }>({ defer: true });

  const hasPaymentMethod = Boolean(paymentMethod?.type);
  const { title, subtitle } = hasPaymentMethod
    ? describePaymentMethod(paymentMethod)
    : { title: 'No payment method', subtitle: undefined };

  const getCardIcon = () => {
    if (!hasPaymentMethod) return <Payment style={{ fontSize: '20px' }} />;
    if (paymentMethod.type === 'card') return <CreditCard style={{ fontSize: '20px' }} />;
    return <AccountBalance style={{ fontSize: '20px' }} />;
  };

  const handleManageDetails = async () => {
    setIsLoading(true);
    try {
      const response = await customerPortalFetch.run({
        url: `${apiUrl}/tm/subscriptions/customer-portal`,
        method: 'POST',
        data: {
          returnUrl: window.location.href,
        },
      });

      if (response?.data?.data?.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography className={classes.sectionHeading}>Billing Method</Typography>
      <Box mt={2}>
        <Box display='flex' alignItems='center' mb={3}>
          <Box
            mr={2}
            display='flex'
            alignItems='center'
            justifyContent='center'
            style={{ color: theme.color.grey.neutralBrand500 }}
          >
            {getCardIcon()}
          </Box>
          <Box>
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '16px',
                color: hasPaymentMethod ? '#333' : shouldAlert ? '#d32f2f' : '#333',
              }}
            >
              {title}
            </Typography>
            {hasPaymentMethod && subtitle && (
              <Typography style={{ fontSize: '14px', color: '#666', textTransform: 'capitalize' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <ConsoleActionButton
          variant='contained'
          onClick={handleManageDetails}
          disabled={isLoading}
          style={{
            backgroundColor: !hasPaymentMethod && shouldAlert ? '#d32f2f' : theme.color.secondary.main900,
            color: '#ffffff',
            boxSizing: 'border-box',
            borderRadius: '4px',
            textTransform: 'none',
            padding: '8px 16px',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} color='inherit' />
          ) : hasPaymentMethod ? (
            'Manage Details'
          ) : (
            'Add Payment Method'
          )}
        </ConsoleActionButton>
      </Box>
    </Box>
  );
};
